import type { TOC } from '@ember/component/template-only';
import { on } from '@ember/modifier';
import {
  PrismAccordion,
  PrismBadge,
  PrismBanner,
  PrismCheckbox,
  PrismIcon,
  PrismNumberInput,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import {
  NUMBER_INPUT_MAX_VALUE,
  OVERRIDE_TYPES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import { getOverrideVariant } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './overbooking-panel.module.css';

interface OverbookingPanelSignature {
  Args: {
    automationOn: boolean;
    effectiveOverbookingWithOverride: number | null;
    errorMessage?: string;
    failedOverbookingValue: number | null;
    formattedOverbooking: number | string;
    hasOverbookingOverride: boolean;
    isOverbooked: boolean;
    isSending: boolean;
    isTouched: boolean;
    onOverbookingBlur: () => void;
    onToggleAutomation: (event: Event) => void;
    onUpdateOverbooking: (value: number | null) => void;
    overbooking: number | null;
    physicalRoomsAvailable: number | string;
    physicalRoomsAvailableLabel: string;
    roomsForSaleValue: string | number;
    showOverbookingException: boolean;
  };
}

const OverbookingPanel = <template>
  <PrismAccordion
    @initiallyOpened={{true}}
    @title={{t "general.hotel"}}
    class="mx-500 my-400"
  >

    <:summaryAfter>

      {{#if @hasOverbookingOverride}}
        <PrismBadge
          @iconName={{getOverrideVariant OVERRIDE_TYPES.OVERBOOKING}}
          @label={{t "pricing_manager.automation_off"}}
          @type="warning"
        />
      {{/if}}
    </:summaryAfter>
    <:default>
      {{#if @showOverbookingException}}
        <div class="mb-400">
          <PrismBanner
            @bannerMessage={{t
              "pricing_assistant.push_error_code.generic_issue"
            }}
            @bannerStyle="critical"
            @bannerTitle={{t
              "pricing_assistant.exception.action_state.overbooking_title_with_value"
              value=@failedOverbookingValue
            }}
            @icon="circle-minus"
          />
        </div>

      {{/if}}
      <div
        class="{{styles.automation-container}}
          mb-400 flex items-center justify-between p-400"
      >
        <PrismCheckbox
          @checked={{@automationOn}}
          @disabled={{@isSending}}
          @onChange={{@onToggleAutomation}}
          data-test-id="overbooking-automation-checkbox"
        >
          <p class={{if @automationOn styles.automation-label-on}}>
            {{t "pricing_manager.automation_on"}}
          </p>
        </PrismCheckbox>
        <div
          class="flex items-center gap-100
            {{unless @automationOn styles.automation-value-off}}"
        >
          <PrismIcon
            @color={{if @automationOn "on-intelligence" "disabled"}}
            @name={{if @automationOn "automation" "automation-off"}}
            @size="small"
          />
          <p class="text-300--regular">
            {{#if @automationOn}}
              {{@formattedOverbooking}}
            {{else}}
              {{t
                "pricing_manager.overbooking.automation_value"
                value=@formattedOverbooking
              }}
            {{/if}}
          </p>
        </div>
      </div>

      <div class="text-300--regular flex flex-col gap-400">
        <div class="flex justify-between">
          <div class="flex items-center gap-100">
            {{t "pricing_manager.overbooking.physical_rooms_available"}}
            <div>
              <PrismIcon
                @name="circle-information-outline"
                @size="extra-small"
              />
              <PrismTooltip
                @side="top"
                @text={{@physicalRoomsAvailableLabel}}
              />
            </div>
          </div>
          <div class="{{if @isOverbooked 'text-critical-default'}} pr-200">
            {{@physicalRoomsAvailable}}
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div>
            {{t "pricing_manager.overbooking.current_overbooking"}}
          </div>
          <div class="{{styles.overbooking-value-wrapper}} flex items-center">
            {{#if @isSending}}
              <div class="loading-pulse w-[177px]"></div>
            {{else if @automationOn}}
              <div
                class="pr-200"
                data-test-id="overbooking-effective-value"
              >{{@effectiveOverbookingWithOverride}}</div>
            {{else}}
              <PrismNumberInput
                @errorMessage={{@errorMessage}}
                @hasErrors={{if @errorMessage true}}
                @isTouched={{@isTouched}}
                @max={{NUMBER_INPUT_MAX_VALUE}}
                @min={{0}}
                @onValueChange={{@onUpdateOverbooking}}
                @removePadding={{true}}
                @value={{@overbooking}}
                class="text-right"
                data-test-id="overbooking-value-input"
                {{on "blur" @onOverbookingBlur}}
              />
            {{/if}}
          </div>
        </div>

        <div class="flex justify-between">
          <div>
            {{t "pricing_manager.overbooking.rooms_for_sale"}}
          </div>
          <div class="pr-200">
            {{@roomsForSaleValue}}
          </div>
        </div>
      </div>
    </:default>
  </PrismAccordion>
</template> satisfies TOC<OverbookingPanelSignature>;

export default OverbookingPanel;
